import { Context } from '@nuxt/types';
import { ISbStoryData } from '@storyblok/js';
import { ArticleStoryblok, LegalPageStoryblok, PageStoryblok } from '~/types/storyblok-generated';
import { getDownloadUrl, slugify } from '~/utility/helpers';
import { transformDate, transformImage, transformLink, transformRichText } from './transformComponents';

interface TransformedPageBase {
  id: number;
  contentType: 'article' | 'page' | 'legalPage' | 'TransformError';
  name: string;
  meta: ReturnType<typeof getMeta>;
  fullSlug: string;
}

export type TransformedRegularPage = TransformedPageBase & Awaited<ReturnType<typeof transformRegularPage>>;
export type TransformedArticlePage = TransformedPageBase & Awaited<ReturnType<typeof transformArticle>>;
export type TransformedArticlePageStage = Awaited<ReturnType<typeof transformArticle>>['stage'];
export type TransformedLegalPage = TransformedPageBase & Awaited<ReturnType<typeof transformLegalPage>>;
export type TransformedErrorPage = TransformedPageBase & {
  type: 'TransformError';
  data: { headline: string; errorDescription: any };
};
export type TransformedPage =
  | TransformedRegularPage
  | TransformedArticlePage
  | TransformedLegalPage
  | TransformedErrorPage;

export type ArticleCategory = 'Neuigkeiten' | 'Referenz' | 'Pressemitteilung';

/**
 * Transforms a standard page
 */
async function transformRegularPage(pageData: ISbStoryData<PageStoryblok>) {
  let parentSlug: any;
  let current: any;
  let fullSlug: any;
  if (pageData.content.navInline === true) {
    fullSlug = pageData.full_slug.split('/');
    parentSlug = fullSlug[fullSlug.length - 2];
    current = pageData.name;
  }

  return {
    navInline: {
      category: parentSlug,
      current: current,
      state: pageData.content.navInline,
    },
  };
}

/**
 * Transforms a legal page
 */
async function transformLegalPage(pageData: ISbStoryData<LegalPageStoryblok>) {
  return {
    stage: {
      headline: {
        level: 'h1',
        text: pageData.content.pageTitle,
      },
      headlineSmall: false,
      variant: 'default' as const,
      news: false,
      theme: 'green' as const,
      video: null,
    },
    pageTitle: {
      level: 'h1',
      text: pageData.content.pageTitle,
    },
    pageContent: transformRichText(pageData.content.pageContent),
  };
}

/**
 * Generates the meta object for the page
 */
function getMeta(data: PageStoryblok | ArticleStoryblok | LegalPageStoryblok, context: Context) {
  const metaEntries = [];
  let title = 'Geiger Gruppe';
  let metaData: PageStoryblok['meta'] | null = null;

  // We habe to check for both 'meta' and 'Meta' because of inconsistent naming in Storyblok
  if ('meta' in data && data.meta) {
    metaData = data.meta;
  }
  if ('Meta' in data && data.Meta) {
    metaData = data.Meta;
  }

  if (metaData) {
    metaEntries.push(
      {
        hid: 'description',
        name: 'description',
        content: metaData.description,
      },
      {
        hid: 'og:title',
        property: 'og:title',
        content: metaData.og_title || metaData.title || 'Geiger Gruppe',
      },
      {
        hid: 'og:description',
        property: 'og:description',
        content: metaData.og_description || metaData.description,
      },
      {
        hid: 'og:url',
        property: 'og:url',
        content:
          context.route.path.slice(-1) === '/'
            ? `${process.env.APP_URL}${context.route.path}`
            : `${process.env.APP_URL}${context.route.path}/`,
      },
      {
        hid: 'twitter:title',
        property: 'twitter:title',
        content: metaData.twitter_title || metaData.title || 'Geiger Gruppe',
      },
      {
        hid: 'robots',
        name: 'robots',
        content: 'noIndex' in data && data.noIndex ? 'noindex' : 'noarchive',
      }
    );

    if (metaData.og_image) {
      metaEntries.push({
        hid: 'og:image',
        property: 'og:image',
        content: `${metaData.og_image}/m/1200x630/smart`,
      });
    }

    if (metaData.twitter_image) {
      metaEntries.push({
        hid: 'twitter:image',
        property: 'twitter:image',
        content: `${metaData.twitter_image}/m/1200x630/smart`,
      });
    }

    if (metaData.title) {
      title = metaData.title;
    }
  }

  return {
    title: title,
    meta: metaEntries.filter(metaItem => metaItem.content && metaItem.content.length > 0),
    link: [
      {
        hid: 'canonical',
        rel: 'canonical',
        href:
          ('canonical' in data &&
            data.canonical?.replace('https://geigergruppe.com', 'https://www.geigergruppe.com')) ||
          (context.route.path.slice(-1) === '/'
            ? `${process.env.APP_URL}${context.route.path}`
            : `${process.env.APP_URL}${context.route.path}/`),
      },
    ],
  };
}

/**
 * Handles an error during page transformation
 */
function transformPageError(pageName: string, error: any) {
  return {
    type: 'TransformError' as const,
    data: {
      headline: `Error for page: ${pageName}`,
      errorDescription: error,
    },
  };
}

/**
 * Transforms an article page
 */
async function transformArticle(pageData: ISbStoryData<ArticleStoryblok>, context: Context) {
  const [statusData, categoriesData] = await Promise.all([
    context.$getDataSourceCache('projektstatus') || context.$loadDataSource('projektstatus'),
    context.$getDataSourceCache('categories') || context.$loadDataSource('categories'),
  ]);

  return {
    stage: {
      headline: {
        level: 'h1',
        fontStyle: pageData.content.headlineSmall ? 'h3' : 'h2',
        text: pageData.content.stageHeadline || undefined,
      },
      headlineSmall: false,
      category: categoriesData[slugify(pageData.content.category)]?.value,
      date: transformDate(pageData.content.date),
      project: statusData[slugify(pageData.content.project ?? '')]?.value || pageData.content.project,
      copy: pageData.content.stageCopy,
      stageTable:
        pageData.content.table?.map((tableItem: any) => ({
          label: tableItem.label,
          content: tableItem.content.replace(/(?:\r\n|\r|\n)/g, '<br />'),
        })) ?? [],
      breadcrumb: transformLink(pageData.content.breadcrumbLink, pageData.content.breadcrumb),
      download: pageData.content.downloadLabel
        ? {
            label: pageData.content.downloadLabel,
            url: getDownloadUrl(pageData.content.downloadLink?.filename ?? ''),
          }
        : undefined,
      button: transformLink(pageData.content.stageLink, pageData.content.stageLabel),
      image: transformImage(pageData.content.stageImage),
      variant: 'default' as const,
      news: true,
      theme: 'green' as const,
      video: null,
    },
    // It seems like storyblok doesn't get the type here because we are using a datasource as the select options
    category: pageData.content.category as ArticleCategory,
    tags: pageData.content.tags,
  };
}

export default async function (
  pageData: ISbStoryData<ArticleStoryblok | PageStoryblok | LegalPageStoryblok>,
  context: Context
): Promise<TransformedPage> {
  async function transformPage(pageData: ISbStoryData<ArticleStoryblok | PageStoryblok | LegalPageStoryblok>) {
    try {
      switch (pageData.content.component) {
        case 'page':
          return transformRegularPage(pageData as ISbStoryData<PageStoryblok>);
        case 'article':
          return await transformArticle(pageData as ISbStoryData<ArticleStoryblok>, context);
        case 'legalPage':
          return transformLegalPage(pageData as ISbStoryData<LegalPageStoryblok>);
        default:
          return transformPageError(pageData.name, 'No transform rule found.');
      }
    } catch (error) {
      return transformPageError(pageData.name, error);
    }
  }

  return {
    id: pageData.id,
    contentType: pageData.content.component,
    name: pageData.name,
    meta: getMeta(pageData.content, context),
    ...(await transformPage(pageData)),
    fullSlug: pageData.full_slug,
  };
}
